import { useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { setupCanvas } from '@/utils/canvasHelpers';
import {
  ALIGNMENT_RINGS,
  RENDERING,
  COMMON_COLORS,
  isLandmarkVisible,
  getOpacityFromIntensity,
  getAlignmentZoneColor,
} from '@/utils/visualizationConstants';

interface Landmark {
  x: number;
  y: number;
  visibility?: number;
}

interface AlignmentRingsOverlayProps {
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  templateLandmarks: Landmark[];
  athleteLandmarks: Landmark[];
  /** Width of the actual video render area */
  width: number;
  /** Height of the actual video render area */
  height: number;
  /** X offset from canvas left edge to the video render area (pillarbox offset) */
  offsetX?: number;
  /** Y offset from canvas top edge to the video render area (letterbox offset) */
  offsetY?: number;
  /** Full container width — canvas is sized to this */
  containerWidth?: number;
  /** Full container height — canvas is sized to this */
  containerHeight?: number;
  intensity: number;
  ringRadii?: [number, number, number];
  showOnlyKeyLandmarks?: boolean;
  className?: string;
}

/**
 * Key landmark indices for major joints
 * Based on MediaPipe Pose landmark model
 */
const KEY_LANDMARKS = [
  11, 12, // shoulders
  13, 14, // elbows
  15, 16, // wrists
  23, 24, // hips
  25, 26, // knees
  27, 28, // ankles
];

/**
 * Get distance between two points
 */
function getDistance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Determine which ring zone a landmark falls into
 * Returns: 0 (inside inner), 1 (middle), 2 (outer), 3 (outside all)
 */
function getRingZone(
  distance: number,
  radii: [number, number, number]
): number {
  if (distance <= radii[0]) return 0;
  if (distance <= radii[1]) return 1;
  if (distance <= radii[2]) return 2;
  return 3;
}

// Removed getZoneColor - now using getAlignmentZoneColor from constants

/**
 * Draw concentric rings centered at a point
 */
function drawTargetRings(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radii: [number, number, number],
  baseAlpha: number
) {
  const ringColor = ALIGNMENT_RINGS.RING_COLOR;

  // Draw rings from outer to inner with decreasing opacity
  const opacities = ALIGNMENT_RINGS.RING_OPACITIES;

  // Draw outer ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, radii[2], 0, 2 * Math.PI);
  ctx.strokeStyle = ringColor;
  ctx.lineWidth = ALIGNMENT_RINGS.LINE_WIDTH;
  ctx.globalAlpha = opacities[2] * baseAlpha;
  ctx.stroke();

  // Draw middle ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, radii[1], 0, 2 * Math.PI);
  ctx.strokeStyle = ringColor;
  ctx.lineWidth = ALIGNMENT_RINGS.LINE_WIDTH;
  ctx.globalAlpha = opacities[1] * baseAlpha;
  ctx.stroke();

  // Draw inner ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, radii[0], 0, 2 * Math.PI);
  ctx.strokeStyle = ringColor;
  ctx.lineWidth = ALIGNMENT_RINGS.LINE_WIDTH;
  ctx.globalAlpha = opacities[0] * baseAlpha;
  ctx.stroke();

  // Reset alpha
  ctx.globalAlpha = 1.0;
}

/**
 * Draw a landmark dot
 */
function drawLandmarkDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  radius: number = 5
) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();

  // Add a subtle border for visibility
  ctx.strokeStyle = COMMON_COLORS.BORDER_WHITE;
  ctx.lineWidth = ALIGNMENT_RINGS.LINE_WIDTH;
  ctx.stroke();
}

/**
 * AlignmentRingsOverlay Component
 *
 * Shows concentric target circles around ATHLETE landmarks.
 * The athlete landmark dot is colored based on how far it is from the template landmark.
 * Green = inside tight tolerance, Yellow = middle, Orange = outer, Red = outside all rings.
 *
 * Note: When opposite-handed comparison is used, templateLandmarks should be pre-mirrored
 * before being passed to this component (handled in parent).
 */
function AlignmentRingsOverlay({
  canvasRef: externalCanvasRef,
  templateLandmarks,
  athleteLandmarks,
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  containerWidth,
  containerHeight,
  intensity,
  ringRadii = ALIGNMENT_RINGS.RADII as unknown as [number, number, number],
  showOnlyKeyLandmarks = false,
  className,
}: AlignmentRingsOverlayProps) {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const canvasW = containerWidth ?? width;
  const canvasH = containerHeight ?? height;

  useEffect(() => {
    const ctx = setupCanvas(canvasRef, canvasW, canvasH);
    if (!ctx) return;

    try {
      // Skip rendering if intensity is too low (nearly invisible)
      if (intensity < RENDERING.MIN_INTENSITY) return;

      // Calculate base alpha from intensity
      const baseAlpha = getOpacityFromIntensity(intensity);

    // Determine which landmarks to show
    const landmarkIndices = showOnlyKeyLandmarks
      ? KEY_LANDMARKS
      : Array.from({ length: Math.max(templateLandmarks.length, athleteLandmarks.length) }, (_, i) => i);

    // Draw rings and landmarks
    landmarkIndices.forEach((index) => {
      const templateLandmark = templateLandmarks[index];
      const athleteLandmark = athleteLandmarks[index];

      // Skip if athlete landmark doesn't exist or has low visibility
      if (!athleteLandmark || !isLandmarkVisible(athleteLandmark)) {
        return;
      }

      // Convert athlete normalized coordinates to canvas coordinates (apply offset)
      const athleteX = athleteLandmark.x * width + offsetX;
      const athleteY = athleteLandmark.y * height + offsetY;

      // Draw target rings centered at ATHLETE position (not template)
      drawTargetRings(ctx, athleteX, athleteY, ringRadii, baseAlpha);

      // Calculate distance from template to determine color
      if (templateLandmark && isLandmarkVisible(templateLandmark)) {
        const templateX = templateLandmark.x * width + offsetX;
        const templateY = templateLandmark.y * height + offsetY;

        // Calculate distance from template
        const distance = getDistance(athleteX, athleteY, templateX, templateY);

        // Determine which zone the athlete landmark is in
        const zone = getRingZone(distance, ringRadii);
        const color = getAlignmentZoneColor(zone);

        // Draw the athlete landmark dot
        ctx.globalAlpha = baseAlpha;
        drawLandmarkDot(ctx, athleteX, athleteY, color, 6);
        ctx.globalAlpha = 1.0;
      } else {
        // If no template landmark available, draw athlete dot in neutral color
        ctx.globalAlpha = baseAlpha;
        drawLandmarkDot(ctx, athleteX, athleteY, COMMON_COLORS.NEUTRAL_GRAY, 6);
        ctx.globalAlpha = 1.0;
      }
    });
    } catch (error) {
      console.error('[AlignmentRingsOverlay] Canvas drawing error:', error);
    }
  }, [
    templateLandmarks,
    athleteLandmarks,
    width,
    height,
    offsetX,
    offsetY,
    canvasW,
    canvasH,
    intensity,
    ringRadii,
    showOnlyKeyLandmarks,
    canvasRef,
  ]);

  // Always render canvas - the useEffect handles drawing to whichever ref is used
  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%' }}
      aria-label="Alignment rings overlay"
    />
  );
}

export default AlignmentRingsOverlay;
